<template>
  <div class="p-2 rounded-xl min-h-screen">
    <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-3 mb-4 shadow-sm border border-gray-100">
      <div class="flex items-center justify-between">
        <div class="text-sm font-extrabold text-gray-800">محصولات ادغام شده</div>
        <button
            class="text-[11px] px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 font-bold"
            :disabled="loading"
            @click="fetchMerged"
        >
          بروزرسانی
        </button>
      </div>
      <div class="text-[11px] text-gray-500 mt-2 leading-5">
        محصولاتی که قبلاً از صفحهٔ «ادغام محصولات تکراری» در یک محصول دیگر ادغام شده‌اند. با «بازگردانی»
        محصول دوباره فعال و در لیست‌ها قابل‌مشاهده می‌شود، اما توجه کنید: موجودی و رفرنس‌هایی (سفارش، POS، انبار)
        که در زمان ادغام به محصول اصلی منتقل شده بودند به‌صورت خودکار برنمی‌گردند و در صورت نیاز باید دستی
        بررسی/اصلاح شوند.
      </div>
      <div class="mt-3 flex gap-2">
        <input
            v-model="search"
            placeholder="جستجو با نام، GTIN یا شناسه محصول"
            class="flex-1 px-3 py-2 bg-gray-100 text-xs rounded-lg border text-right"
            @keyup.enter="applySearch"
        >
        <button
            class="text-[11px] px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-bold flex-shrink-0"
            :disabled="loading"
            @click="applySearch"
        >
          جستجو
        </button>
        <button
            v-if="search"
            class="text-[11px] px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 font-bold flex-shrink-0"
            @click="search = ''; applySearch()"
        >
          پاک کردن
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center text-xs text-gray-400 py-10">در حال بارگذاری...</div>
    <div v-else-if="!items.length" class="text-center text-xs text-gray-400 py-10">
      هیچ محصول ادغام‌شده‌ای یافت نشد.
    </div>

    <div v-else class="space-y-2">
      <div
          v-for="item in items"
          :key="item.id"
          class="bg-white rounded-xl border border-gray-100 shadow-sm p-3 flex items-center gap-3"
      >
        <img :src="img_source(item.image)" class="w-12 h-12 rounded-lg object-cover border border-gray-200 flex-shrink-0">
        <div class="flex-1 min-w-0">
          <div class="text-[12px] font-bold text-gray-800 truncate">{{ item.name }}</div>
          <div class="text-[10px] text-gray-400">#{{ item.id }} · {{ fmtNum(item.price) }} تومان</div>
        </div>

        <div class="text-gray-300 text-lg flex-shrink-0">←</div>

        <div class="flex items-center gap-2 flex-1 min-w-0">
          <img
              v-if="item.merged_into"
              :src="img_source(item.merged_into.image)"
              class="w-12 h-12 rounded-lg object-cover border border-emerald-200 flex-shrink-0"
          >
          <div class="min-w-0">
            <div class="text-[12px] font-bold text-emerald-700 truncate">
              {{ item.merged_into ? item.merged_into.name : '—' }}
            </div>
            <div v-if="item.merged_into" class="text-[10px] text-gray-400">
              #{{ item.merged_into.id }} · {{ statusLabel(item.merged_into.status) }}
              <span v-if="!item.merged_into.active" class="text-rose-500 font-bold">(غیرفعال)</span>
            </div>
          </div>
        </div>

        <button
            class="px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 text-[11px] font-bold hover:bg-amber-100 flex-shrink-0 disabled:opacity-40"
            :disabled="busyIds.has(item.id)"
            @click="confirmReactivate(item)"
        >
          بازگردانی
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdminMergedProducts',
  data() {
    return {
      loading: false,
      search: '',
      items: [],
      busyIds: new Set(),
    };
  },
  mounted() {
    this.fetchMerged();
  },
  methods: {
    applySearch() {
      this.fetchMerged();
    },
    fetchMerged() {
      this.loading = true;
      const params = { limit: 50, offset: 0 };
      if (this.search && this.search.trim()) params.search = this.search.trim();
      this.request({
        url: this.endpoint('product/adminMergedProducts'),
        method: 'get',
        params,
        success: (data) => {
          this.items = data.results || [];
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
    },
    statusLabel(status) {
      const map = {
        pr: 'نیاز به بررسی',
        pending: 'در انتظار تایید',
        draft: 'پیش‌نویس',
        publish: 'منتشر شده',
      };
      return map[status] || status;
    },
    fmtNum(n) {
      return (n || 0).toLocaleString('fa-IR');
    },
    confirmReactivate(item) {
      const mainName = item.merged_into ? item.merged_into.name : '';
      if (!confirm(`محصول «${item.name}» دوباره فعال شود؟ توجه: موجودی و رفرنس‌هایی که به «${mainName}» منتقل شده‌اند خودکار برنمی‌گردند.`)) {
        return;
      }
      this.busyIds = new Set(this.busyIds).add(item.id);
      this.request({
        url: this.endpoint(`product/adminProductReactivate/${item.id}`),
        method: 'post',
        success: () => {
          const remaining = new Set(this.busyIds);
          remaining.delete(item.id);
          this.busyIds = remaining;
          this.notify('محصول دوباره فعال شد', 'success');
          this.items = this.items.filter((i) => i.id !== item.id);
        },
        error: () => {
          const remaining = new Set(this.busyIds);
          remaining.delete(item.id);
          this.busyIds = remaining;
        },
      });
    },
  },
};
</script>
